import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import { slugify, type Translatable } from '@hrlabs/shared-types';
import { insightSaveSchema } from '@hrlabs/validation';
import { getRow, listCollection, saveRow } from '../../lib/db';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import { BilingualField } from '../../components/BilingualField';

const empty = (): Translatable => ({ en: '', bg: '' });
const tr = (v: unknown): Translatable => {
  const o = (v ?? {}) as Partial<Translatable>;
  return { en: o.en ?? '', bg: o.bg ?? '' };
};
const today = () => new Date().toISOString().slice(0, 10);

export function InsightEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notify } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [slug, setSlug] = useState<Translatable>(empty());
  const [title, setTitle] = useState<Translatable>(empty());
  const [answer, setAnswer] = useState<Translatable>(empty());
  const [body, setBody] = useState<Translatable>(empty());
  const [topic, setTopic] = useState<Translatable>(empty());
  const [authorName, setAuthorName] = useState('');
  const [readMinutes, setReadMinutes] = useState(5);
  const [publishedAt, setPublishedAt] = useState(today());

  useEffect(() => {
    if (!id) return;
    getRow('insights', id).then((r) => {
      if (r) {
        setStatus(r.status === 'published' ? 'published' : 'draft');
        setSlug(tr(r.slug)); setTitle(tr(r.title)); setAnswer(tr(r.answer)); setBody(tr(r.body)); setTopic(tr(r.topic));
        setAuthorName(String(r.authorName ?? ''));
        setReadMinutes(Number(r.readMinutes ?? 5) || 5);
        const pa = r.publishedAt as { toDate?: () => Date } | undefined;
        if (pa?.toDate) setPublishedAt(pa.toDate().toISOString().slice(0, 10));
      }
      setLoading(false);
    });
  }, [id]);

  // Suggest a slug from the English title until the author edits it.
  const onTitle = (v: Translatable) => {
    setTitle(v);
    if (!slug.en || slug.en === slugify(title.en)) setSlug((s) => ({ ...s, en: slugify(v.en) }));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = {
      slug, title, answer, body, topic,
      authorName: authorName.trim() || user?.displayName || 'HRLabs',
      readMinutes, status, publishedAt,
    };
    const parsed = insightSaveSchema.safeParse(payload);
    if (!parsed.success) {
      setErrors(parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`));
      notify('danger', 'Please fix the highlighted problems.');
      return;
    }
    // Slugs must be unique per language across all insights.
    const others = await listCollection('insights');
    for (const l of ['en', 'bg'] as const) {
      const s = parsed.data.slug[l];
      if (s && others.some((o) => o.id !== id && tr(o.slug)[l] === s)) {
        setErrors([`slug.${l}: "${s}" is already used by another article`]);
        notify('danger', 'Slug already in use.');
        return;
      }
    }
    setErrors([]);
    setSaving(true);
    try {
      const { publishedAt: date, ...rest } = parsed.data;
      await saveRow('insights', id, {
        ...rest,
        publishedAt: Timestamp.fromDate(new Date(`${date}T09:00:00Z`)),
      });
      notify('success', parsed.data.status === 'published' ? 'Saved and published.' : 'Saved as draft.');
      navigate('/insights');
    } catch (err) {
      notify('danger', (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-muted">Loading…</p>;
  return (
    <form onSubmit={onSubmit}>
      <p className="mb-1"><Link to="/insights">← Insights</Link></p>
      <h3 className="fw-bold mb-3">{id ? 'Edit article' : 'New article'}</h3>

      {errors.length > 0 && (
        <div className="alert alert-warning">
          <strong>Cannot save:</strong>
          <ul className="mb-0">{errors.map((x) => (<li key={x}>{x}</li>))}</ul>
        </div>
      )}
      <p className="small text-muted">
        Write in English, Bulgarian or both. A language you leave completely empty simply has no page.
      </p>

      <div className="row g-2 mb-3">
        <div className="col-md-3">
          <label className="form-label fw-semibold">Status</label>
          <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="draft">draft</option><option value="published">published</option>
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label fw-semibold">Publication date</label>
          <input type="date" className="form-control" value={publishedAt} onChange={(e) => setPublishedAt(e.target.value)} />
        </div>
        <div className="col-md-3">
          <label className="form-label fw-semibold">Author</label>
          <input className="form-control" value={authorName} onChange={(e) => setAuthorName(e.target.value)} placeholder="Radoslava Dimitrova" />
        </div>
        <div className="col-md-3">
          <label className="form-label fw-semibold">Reading time (min)</label>
          <input type="number" min={1} max={60} className="form-control" value={readMinutes} onChange={(e) => setReadMinutes(Number(e.target.value))} />
        </div>
      </div>

      <BilingualField label="Title" value={title} onChange={onTitle} rows={1} />
      <BilingualField label="URL slug (lowercase Latin letters, digits, dashes)" value={slug} onChange={setSlug} rows={1} />
      <BilingualField label="Topic label (e.g. Cost of hiring)" value={topic} onChange={setTopic} rows={1} />
      <BilingualField label="Answer-first summary (40–80 words — shown on top, in the listing and in search results)" value={answer} onChange={setAnswer} rows={4} />
      <BilingualField label="Body" value={body} onChange={setBody} rich source />

      <button className="btn btn-success" disabled={saving} type="submit">{saving ? 'Saving…' : 'Save'}</button>
    </form>
  );
}
